import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  Popconfirm,
  Tabs,
  Skeleton,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
  GlobalOutlined,
  CoffeeOutlined,
  BankOutlined,
  CompassOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllCities,
  getCity,
  createCity,
  updateCity,
  deleteCity,
  getActivitiesByCity,
  getMealsByCity,
  getHotelsByCity,
  getAllActivities,
  getAllMeals,
  getAllHotels,
  createActivity,
  updateActivity,
  deleteActivity,
  createMeal,
  updateMeal,
  deleteMeal,
  createHotel,
  updateHotel,
  deleteHotel,
  pushListBuilderToBackend,
} from '../../services/listBuilderService';
import type {
  ListBuilderCity,
  ListBuilderActivity,
  ListBuilderMeal,
  ListBuilderHotel,
} from '../../types/listBuilder';
import { listBuilderEn } from '../../content/listBuilder.en';
import ActivityFormModal from '../../components/ActivityFormModal/ActivityFormModal';
import MealFormModal from '../../components/MealFormModal/MealFormModal';
import HotelFormModal from '../../components/HotelFormModal/HotelFormModal';
import styles from './ListBuilder.module.css';

const CityFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (values: { name: string; country: string; region?: string; notes?: string; history?: { summary: string } }) => void;
  initial?: ListBuilderCity | null;
}> = ({ open, onClose, onSave, initial }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: initial?.name ?? '',
        country: initial?.country ?? '',
        region: initial?.region ?? '',
        notes: initial?.notes ?? '',
        historySummary: initial?.history?.summary ?? '',
      });
    }
  }, [open, initial, form]);

  const confirmClose = useCallback(() => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: () => { form.resetFields(); onClose(); },
      });
    } else {
      onClose();
    }
  }, [form, onClose]);

  return (
    <Modal
      open={open}
      title={initial ? listBuilderEn.cities.editCity : listBuilderEn.cities.addCity}
      onCancel={confirmClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={(v) => {
        onSave({
          name: v.name,
          country: v.country,
          region: v.region,
          notes: v.notes,
          history: { summary: v.historySummary?.trim() ?? '' },
        });
        form.resetFields();
        onClose();
      }}>
        <Form.Item name="name" label={listBuilderEn.cities.form.name} rules={[{ required: true }]}>
          <Input placeholder={listBuilderEn.cities.form.namePlaceholder} />
        </Form.Item>
        <Form.Item name="country" label={listBuilderEn.cities.form.country} rules={[{ required: true }]}>
          <Input placeholder={listBuilderEn.cities.form.countryPlaceholder} />
        </Form.Item>
        <Form.Item name="region" label={listBuilderEn.cities.form.region}>
          <Input placeholder={listBuilderEn.cities.form.regionPlaceholder} />
        </Form.Item>
        <Form.Item name="historySummary" label={listBuilderEn.cities.form.historySummary}>
          <Input.TextArea rows={3} placeholder={listBuilderEn.cities.form.historySummaryPlaceholder} />
        </Form.Item>
        <Form.Item name="notes" label={listBuilderEn.cities.form.notes}>
          <Input.TextArea rows={3} placeholder={listBuilderEn.cities.form.notesPlaceholder} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit">{listBuilderEn.buttons.save}</Button>
          <Button onClick={confirmClose} style={{ marginLeft: 8 }}>{listBuilderEn.buttons.cancel}</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ListBuilder: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, isDataSyncing } = useAuth();
  const [cities, setCities] = useState<ListBuilderCity[]>([]);
  const [activities, setActivities] = useState<ListBuilderActivity[]>([]);
  const [meals, setMeals] = useState<ListBuilderMeal[]>([]);
  const [hotels, setHotels] = useState<ListBuilderHotel[]>([]);
  const hasPushedRef = useRef(false);

  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<ListBuilderCity | null>(null);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityCityId, setActivityCityId] = useState('');
  const [editingActivity, setEditingActivity] = useState<ListBuilderActivity | null>(null);
  const [mealModalOpen, setMealModalOpen] = useState(false);
  const [mealCityId, setMealCityId] = useState('');
  const [editingMeal, setEditingMeal] = useState<ListBuilderMeal | null>(null);
  const [hotelModalOpen, setHotelModalOpen] = useState(false);
  const [hotelCityId, setHotelCityId] = useState('');
  const [editingHotel, setEditingHotel] = useState<ListBuilderHotel | null>(null);
  const [activeTab, setActiveTab] = useState('byCity');

  const load = useCallback(() => {
    if (!user?.sub) return;
    setCities(getAllCities(user.sub));
    setActivities(getAllActivities(user.sub));
    setMeals(getAllMeals(user.sub));
    setHotels(getAllHotels(user.sub));
  }, [user?.sub]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.sub && !isDataSyncing) {
      load();
      // Push existing localStorage data to backend on first load
      if (!hasPushedRef.current) {
        hasPushedRef.current = true;
        pushListBuilderToBackend(user.sub);
      }
    } else if (!isAuthenticated) {
      setCities([]);
      setActivities([]);
      setMeals([]);
      setHotels([]);
    }
  }, [isAuthenticated, user?.sub, isDataSyncing, load]);

  const getCityName = (cityId: string): string => {
    const city = getCity(user?.sub ?? '', cityId);
    return city?.name ?? '—';
  };

  const handleSaveCity = (values: { name: string; country: string; region?: string; notes?: string; history?: { summary: string } }): void => {
    if (!user?.sub) return;
    if (editingCity) {
      updateCity(user.sub, editingCity.id, values);
    } else {
      createCity(user.sub, {
        ...values,
        region: values.region ?? '',
        notes: values.notes ?? '',
        history: values.history ?? { summary: '' },
      });
    }
    setEditingCity(null);
    load();
  };

  const handleDeleteCity = (city: ListBuilderCity): void => {
    if (!user?.sub) return;
    deleteCity(user.sub, city.id);
    load();
  };

  const handleSaveActivity = (values: Partial<ListBuilderActivity> & { cityId: string }): void => {
    if (!user?.sub || !values.cityId) return;
    if (editingActivity) {
      updateActivity(user.sub, editingActivity.id, values);
    } else {
      createActivity(user.sub, { ...values, title: values.title ?? '', cityId: values.cityId });
    }
    setEditingActivity(null);
    setActivityCityId('');
    load();
  };

  const handleSaveMeal = (values: Partial<ListBuilderMeal> & { cityId: string }): void => {
    if (!user?.sub || !values.cityId) return;
    if (editingMeal) {
      updateMeal(user.sub, editingMeal.id, values);
    } else {
      createMeal(user.sub, { ...values, name: values.name ?? '', cityId: values.cityId });
    }
    setEditingMeal(null);
    setMealCityId('');
    load();
  };

  const handleSaveHotel = (values: Partial<ListBuilderHotel> & { cityId: string }): void => {
    if (!user?.sub || !values.cityId) return;
    if (editingHotel) {
      updateHotel(user.sub, editingHotel.id, values);
    } else {
      createHotel(user.sub, { ...values, name: values.name ?? '', cityId: values.cityId });
    }
    setEditingHotel(null);
    setHotelCityId('');
    load();
  };

  const renderActivityList = (
    items: ListBuilderActivity[],
    _getCityId: () => string,
    _onAdd: () => void,
    onEdit: (a: ListBuilderActivity) => void,
    onDelete: (a: ListBuilderActivity) => void,
    showCity?: boolean
  ) => {
    if (items.length === 0) {
      return (
        <div className={styles.emptyItemList}>{listBuilderEn.activities.noActivities}</div>
      );
    }
    return (
      <div className={styles.itemList}>
        {items.map((a) => (
          <div key={a.id} className={styles.itemRow}>
            <div className={styles.itemInfo}>
              <div className={styles.itemName}>{a.title}</div>
              <div className={styles.itemMeta}>
                {[showCity ? listBuilderEn.allItems.inCity(getCityName(a.cityId)) : null, a.area, a.priceCategory].filter(Boolean).join(' • ')}
              </div>
            </div>
            <div className={styles.itemActions}>
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(a)} />
              <Popconfirm title={listBuilderEn.confirmDelete(a.title)} onConfirm={() => onDelete(a)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMealList = (
    items: ListBuilderMeal[],
    _onAdd: () => void,
    onEdit: (m: ListBuilderMeal) => void,
    onDelete: (m: ListBuilderMeal) => void,
    showCity?: boolean
  ) => {
    if (items.length === 0) {
      return <div className={styles.emptyItemList}>{listBuilderEn.meals.noMeals}</div>;
    }
    return (
      <div className={styles.itemList}>
        {items.map((m) => (
          <div key={m.id} className={styles.itemRow}>
            <div className={styles.itemInfo}>
              <div className={styles.itemName}>{m.name}</div>
              <div className={styles.itemMeta}>
                {[showCity ? listBuilderEn.allItems.inCity(getCityName(m.cityId)) : null, m.cuisine, m.priceCategory].filter(Boolean).join(' • ')}
              </div>
            </div>
            <div className={styles.itemActions}>
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(m)} />
              <Popconfirm title={listBuilderEn.confirmDelete(m.name)} onConfirm={() => onDelete(m)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderHotelList = (
    items: ListBuilderHotel[],
    _onAdd: () => void,
    onEdit: (h: ListBuilderHotel) => void,
    onDelete: (h: ListBuilderHotel) => void,
    showCity?: boolean
  ) => {
    if (items.length === 0) {
      return <div className={styles.emptyItemList}>{listBuilderEn.hotels.noHotels}</div>;
    }
    return (
      <div className={styles.itemList}>
        {items.map((h) => (
          <div key={h.id} className={styles.itemRow}>
            <div className={styles.itemInfo}>
              <div className={styles.itemName}>{h.name}</div>
              <div className={styles.itemMeta}>
                {[showCity ? listBuilderEn.allItems.inCity(getCityName(h.cityId)) : null, h.area, h.budgetCategory].filter(Boolean).join(' • ')}
              </div>
            </div>
            <div className={styles.itemActions}>
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(h)} />
              <Popconfirm title={listBuilderEn.confirmDelete(h.name)} onConfirm={() => onDelete(h)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /** Group cities by country (sorted), with cities sorted alphabetically within each country */
  const citiesByCountry = useMemo(() => {
    const grouped: Record<string, ListBuilderCity[]> = {};
    cities.forEach((city) => {
      const country = city.country || listBuilderEn.cities.unknownCountry;
      if (!grouped[country]) grouped[country] = [];
      grouped[country].push(city);
    });
    const sortedCountries = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return sortedCountries.map((country) => ({
      country,
      cities: grouped[country].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [cities]);

  if (authLoading || isDataSyncing) {
    return (
      <div className={styles.listBuilder}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div>
              <Skeleton.Button active size="small" style={{ width: 100, marginBottom: 12 }} />
              <Skeleton.Input active size="large" style={{ width: 180, marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: 400 }} />
            </div>
          </div>
          <div className={styles.stats}>
            <Skeleton.Button active size="small" style={{ width: 70 }} />
            <Skeleton.Button active size="small" style={{ width: 80 }} />
            <Skeleton.Button active size="small" style={{ width: 60 }} />
            <Skeleton.Button active size="small" style={{ width: 70 }} />
          </div>
          <div style={{ marginTop: 24 }}>
            <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 16 }} />
            <div className={styles.skeletonCard}>
              <Skeleton.Input active style={{ width: 150, marginBottom: 12 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40, marginBottom: 8 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40, marginBottom: 8 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40 }} />
            </div>
            <div className={styles.skeletonCard}>
              <Skeleton.Input active style={{ width: 120, marginBottom: 12 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40, marginBottom: 8 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40, marginBottom: 8 }} />
              <Skeleton.Button active size="small" style={{ width: '100%', height: 40 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalItems = activities.length + meals.length + hotels.length;
  const hasAnyData = cities.length > 0 || totalItems > 0;

  return (
    <div className={styles.listBuilder}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <Link to="/clients" className={styles.backLink}>
              <ArrowLeftOutlined /> {listBuilderEn.backToClients}
            </Link>
            <h1 className={styles.title}>{listBuilderEn.pageTitle}</h1>
            <p className={styles.subtitle}>{listBuilderEn.pageSubtitle}</p>
          </div>
        </div>

        {hasAnyData && (
          <div className={styles.stats}>
            <span className={styles.statBadge}>{cities.length} {listBuilderEn.stats.cities}</span>
            <span className={styles.statBadge}>{activities.length} {listBuilderEn.stats.activities}</span>
            <span className={styles.statBadge}>{meals.length} {listBuilderEn.stats.meals}</span>
            <span className={styles.statBadge}>{hotels.length} {listBuilderEn.stats.hotels}</span>
          </div>
        )}

        {!hasAnyData ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyTitle}>{listBuilderEn.emptyState.title}</div>
            <div className={styles.emptyDescription}>{listBuilderEn.emptyState.description}</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCityModalOpen(true)}>
              {listBuilderEn.cities.addCity}
            </Button>
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className={styles.tabs}
            items={[
              {
                key: 'byCity',
                label: <span><GlobalOutlined /> {listBuilderEn.nav.byCity}</span>,
                children: (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => setCityModalOpen(true)}>
                        {listBuilderEn.cities.addCity}
                      </Button>
                    </div>
                    {cities.length === 0 ? (
                      <div className={styles.emptyState}>
                        <div className={styles.emptyTitle}>{listBuilderEn.cities.noCities}</div>
                        <div className={styles.emptyDescription}>{listBuilderEn.cities.noCitiesDescription}</div>
                      </div>
                    ) : (
                      citiesByCountry.map(({ country, cities: countryCities }) => (
                        <div key={country} className={styles.countrySection}>
                          <h2 className={styles.countryTitle}>
                            <GlobalOutlined /> {country}
                          </h2>
                          <Collapse
                            defaultActiveKey={countryCities.map((c) => c.id)}
                            items={countryCities.map((city) => {
                              const cityActivities = getActivitiesByCity(user?.sub ?? '', city.id);
                              const cityMeals = getMealsByCity(user?.sub ?? '', city.id);
                              const cityHotels = getHotelsByCity(user?.sub ?? '', city.id);
                              return {
                                key: city.id,
                                label: (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>{city.name}{city.region ? `, ${city.region}` : ''}</span>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={(e) => { e.stopPropagation(); setEditingCity(city); setCityModalOpen(true); }}
                                    />
                                    <Popconfirm
                                      title={listBuilderEn.confirmDeleteCity(city.name)}
                                      onConfirm={(e) => { e?.stopPropagation(); handleDeleteCity(city); }}
                                      onCancel={(e) => e?.stopPropagation()}
                                    >
                                      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                                    </Popconfirm>
                                  </div>
                                ),
                                children: (
                                  <Collapse
                                    accordion={false}
                                    defaultActiveKey={[]}
                                    items={[
                                      {
                                        key: 'activities',
                                        label: (
                                          <span>
                                            <CompassOutlined /> {listBuilderEn.activities.title} ({cityActivities.length})
                                            <Button
                                              type="link"
                                              size="small"
                                              icon={<PlusOutlined />}
                                              onClick={(e) => { e.stopPropagation(); setActivityCityId(city.id); setEditingActivity(null); setActivityModalOpen(true); }}
                                            >
                                              {listBuilderEn.activities.addActivity}
                                            </Button>
                                          </span>
                                        ),
                                        children: renderActivityList(
                                          cityActivities,
                                          () => city.id,
                                          () => { setActivityCityId(city.id); setEditingActivity(null); setActivityModalOpen(true); },
                                          (a) => { setEditingActivity(a); setActivityCityId(city.id); setActivityModalOpen(true); },
                                          (a) => { if (user?.sub) deleteActivity(user.sub, a.id); load(); },
                                          false
                                        ),
                                      },
                                      {
                                        key: 'meals',
                                        label: (
                                          <span>
                                            <CoffeeOutlined /> {listBuilderEn.meals.title} ({cityMeals.length})
                                            <Button
                                              type="link"
                                              size="small"
                                              icon={<PlusOutlined />}
                                              onClick={(e) => { e.stopPropagation(); setMealCityId(city.id); setEditingMeal(null); setMealModalOpen(true); }}
                                            >
                                              {listBuilderEn.meals.addMeal}
                                            </Button>
                                          </span>
                                        ),
                                        children: renderMealList(
                                          cityMeals,
                                          () => { setMealCityId(city.id); setEditingMeal(null); setMealModalOpen(true); },
                                          (m) => { setEditingMeal(m); setMealCityId(m.cityId); setMealModalOpen(true); },
                                          (m) => { if (user?.sub) deleteMeal(user.sub, m.id); load(); },
                                          false
                                        ),
                                      },
                                      {
                                        key: 'hotels',
                                        label: (
                                          <span>
                                            <BankOutlined /> {listBuilderEn.hotels.title} ({cityHotels.length})
                                            <Button
                                              type="link"
                                              size="small"
                                              icon={<PlusOutlined />}
                                              onClick={(e) => { e.stopPropagation(); setHotelCityId(city.id); setEditingHotel(null); setHotelModalOpen(true); }}
                                            >
                                              {listBuilderEn.hotels.addHotel}
                                            </Button>
                                          </span>
                                        ),
                                        children: renderHotelList(
                                          cityHotels,
                                          () => { setHotelCityId(city.id); setEditingHotel(null); setHotelModalOpen(true); },
                                          (h) => { setEditingHotel(h); setHotelCityId(city.id); setHotelModalOpen(true); },
                                          (h) => { if (user?.sub) deleteHotel(user.sub, h.id); load(); },
                                          false
                                        ),
                                      },
                                    ]}
                                  />
                                ),
                              };
                            })}
                          />
                        </div>
                      ))
                    )}
                  </>
                ),
              },
              {
                key: 'allItems',
                label: <span><UnorderedListOutlined /> {listBuilderEn.nav.allItems}</span>,
                children: (
                  <Collapse
                    defaultActiveKey={['activities', 'meals', 'hotels']}
                    items={[
                      {
                        key: 'activities',
                        label: (
                          <span>
                            <CompassOutlined /> {listBuilderEn.allItems.activities} ({activities.length})
                            <Button
                              type="link"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={(e) => { e.stopPropagation(); setActivityCityId(cities[0]?.id ?? ''); setEditingActivity(null); setActivityModalOpen(true); }}
                            >
                              {listBuilderEn.activities.addActivity}
                            </Button>
                          </span>
                        ),
                        children: renderActivityList(
                          activities,
                          () => '',
                          () => { setActivityCityId(cities[0]?.id ?? ''); setEditingActivity(null); setActivityModalOpen(true); },
                          (a) => { setEditingActivity(a); setActivityCityId(a.cityId); setActivityModalOpen(true); },
                          (a) => { if (user?.sub) deleteActivity(user.sub, a.id); load(); },
                          true
                        ),
                      },
                      {
                        key: 'meals',
                        label: (
                          <span>
                            <CoffeeOutlined /> {listBuilderEn.allItems.meals} ({meals.length})
                            <Button
                              type="link"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={(e) => { e.stopPropagation(); setMealCityId(cities[0]?.id ?? ''); setEditingMeal(null); setMealModalOpen(true); }}
                            >
                              {listBuilderEn.meals.addMeal}
                            </Button>
                          </span>
                        ),
                        children: renderMealList(
                          meals,
                          () => { setMealCityId(cities[0]?.id ?? ''); setEditingMeal(null); setMealModalOpen(true); },
                          (m) => { setEditingMeal(m); setMealCityId(m.cityId); setMealModalOpen(true); },
                          (m) => { if (user?.sub) deleteMeal(user.sub, m.id); load(); },
                          true
                        ),
                      },
                      {
                        key: 'hotels',
                        label: (
                          <span>
                            <BankOutlined /> {listBuilderEn.allItems.hotels} ({hotels.length})
                            <Button
                              type="link"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={(e) => { e.stopPropagation(); setHotelCityId(cities[0]?.id ?? ''); setEditingHotel(null); setHotelModalOpen(true); }}
                            >
                              {listBuilderEn.hotels.addHotel}
                            </Button>
                          </span>
                        ),
                        children: renderHotelList(
                          hotels,
                          () => { setHotelCityId(cities[0]?.id ?? ''); setEditingHotel(null); setHotelModalOpen(true); },
                          (h) => { setEditingHotel(h); setHotelCityId(h.cityId); setHotelModalOpen(true); },
                          (h) => { if (user?.sub) deleteHotel(user.sub, h.id); load(); },
                          true
                        ),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        )}

        <CityFormModal open={cityModalOpen} onClose={() => { setCityModalOpen(false); setEditingCity(null); }} onSave={handleSaveCity} initial={editingCity} />
        <ActivityFormModal
          open={activityModalOpen}
          onClose={() => { setActivityModalOpen(false); setEditingActivity(null); setActivityCityId(''); }}
          onSave={handleSaveActivity}
          cities={cities}
          cityId={activityCityId}
          initial={editingActivity}
        />
        <MealFormModal
          open={mealModalOpen}
          onClose={() => { setMealModalOpen(false); setEditingMeal(null); setMealCityId(''); }}
          onSave={handleSaveMeal}
          cities={cities}
          cityId={mealCityId}
          initial={editingMeal}
        />
        <HotelFormModal
          open={hotelModalOpen}
          onClose={() => { setHotelModalOpen(false); setEditingHotel(null); setHotelCityId(''); }}
          onSave={handleSaveHotel}
          cities={cities}
          cityId={hotelCityId}
          initial={editingHotel}
        />
      </div>
    </div>
  );
};

export default ListBuilder;
